/**
 * Input Validation Utilities
 * Centralized validation functions for form inputs
 */

// Quantity Validation Functions

/**
 * Validate quantity input (positive integer)
 */
export const validateQuantity = (value: string | number): boolean => {
  if (value === '' || value === null || value === undefined) {
    return false;
  }
  
  const num = typeof value === 'number' ? value : Number(value);
  
  return (
    !isNaN(num) &&
    num > 0 &&
    Number.isInteger(num) &&
    num <= Number.MAX_SAFE_INTEGER
  );
};

/**
 * Validate and normalize quantity value
 */
export const normalizeQuantity = (value: string | number): number | null => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  
  const num = typeof value === 'number' ? value : Number(value);
  
  if (isNaN(num) || num < 1 || !Number.isInteger(num)) {
    return null;
  }
  
  return num;
};

/**
 * Get quantity validation error message
 */
export const getQuantityError = (value: string | number): string | null => {
  if (value === '' || value === null || value === undefined) {
    return 'Quantity is required';
  }
  
  const num = typeof value === 'number' ? value : Number(value);
  
  if (isNaN(num)) {
    return 'Quantity must be a number';
  }
  
  if (num < 1) {
    return 'Quantity must be greater than 0';
  }
  
  if (!Number.isInteger(num)) {
    return 'Quantity must be a whole number';
  }
  
  if (num > Number.MAX_SAFE_INTEGER) {
    return 'Quantity is too large';
  }
  
  return null;
};

//Barcode Validation 

/**
 * Validate barcode format (EAN-13, UPC, or custom)
 */
export const validateBarcode = (barcode: string): boolean => {
  if (!barcode || barcode.trim() === '') {
    return false;
  }
  
  // EAN-13: 13 digits
  // UPC: 12 digits
  // Allow custom barcodes: 6-20 alphanumeric characters
  const ean13Pattern = /^\d{13}$/;
  const upcPattern = /^\d{12}$/;
  const customPattern = /^[A-Za-z0-9]{6,20}$/;
  
  return ean13Pattern.test(barcode) || upcPattern.test(barcode) || customPattern.test(barcode);
};

/**
 * Get barcode validation error message
 */
export const getBarcodeError = (barcode: string): string | null => {
  if (!barcode || barcode.trim() === '') {
    return 'Barcode is required';
  }
  
  if (barcode.length < 12) {
    return 'Barcode must be at least 12 characters';
  }
  
  if (barcode.length > 20) {
    return 'Barcode must be 20 characters or less';
  }
  
  if (!validateBarcode(barcode)) {
    return 'Barcode must be 12 or 13 digits';
  }
  
  return null;
};

// Email Validation

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  if (!email || email.trim() === '') {
    return false;
  }
  
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email.trim());
};

/**
 * Get email validation error message
 */
export const getEmailError = (email: string): string | null => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  if (!validateEmail(email)) {
    return 'Please enter a valid email address';
  }
  
  return null;
};

//Password Validation

/**
 * Validate password strength
 */
export const validatePassword = (password: string, minLength: number = 8): boolean => {
  if (!password || password.length < minLength) {
    return false;
  }
  
  // At least one letter and one number
  const hasLetter = /[A-Za-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  
  return hasLetter && hasNumber;
};

/**
 * Get password validation error message
 */
export const getPasswordError = (password: string, minLength: number = 8): string | null => {
  if (!password || password.trim() === '') {
    return 'Password is required';
  }
  
  if (password.length < minLength) {
    return `Password must be at least ${minLength} characters`;
  }
  
  if (!validatePassword(password, minLength)) {
    return 'Password must contain at least one letter and one number';
  }
  
  return null;
};

// Name Validation

/**
 * Validate name (product name, user name, etc.)
 */
export const validateName = (name: string, minLength: number = 2, maxLength: number = 255): boolean => {
  if (!name || name.trim() === '') {
    return false;
  }
  
  const trimmed = name.trim();
  return trimmed.length >= minLength && trimmed.length <= maxLength;
};

/**
 * Get name validation error message
 */
export const getNameError = (name: string, fieldName: string = 'Name', minLength: number = 2, maxLength: number = 255): string | null => {
  if (!name || name.trim() === '') {
    return `${fieldName} is required`;
  }
  
  const trimmed = name.trim();
  
  if (trimmed.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  
  if (trimmed.length > maxLength) {
    return `${fieldName} must be ${maxLength} characters or less`;
  }
  
  return null;
};

// Price Validation

/**
 * Validate price (non-negative number, up to 2 decimal places)
 */
export const validatePrice = (price: string | number): boolean => {
  if (price === '' || price === null || price === undefined) {
    return false;
  }
  
  const num = typeof price === 'number' ? price : Number(price);
  
  if (isNaN(num) || num < 0) {
    return false;
  }
  
  // Check decimal places
  if (typeof price === 'string') {
    const decimalParts = price.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > 2) {
      return false;
    }
  }
  
  return true;
};

/**
 * Get price validation error message
 */
export const getPriceError = (price: string | number): string | null => {
  if (price === '' || price === null || price === undefined) {
    return null; // Price is optional
  }
  
  const num = typeof price === 'number' ? price : Number(price);
  
  if (isNaN(num)) {
    return 'Price must be a number';
  }
  
  if (num < 0) {
    return 'Price cannot be negative';
  }
  
  if (typeof price === 'string') {
    const decimalParts = price.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > 2) {
      return 'Price can have at most 2 decimal places';
    }
  }
  
  return null;
};

// SKU Validation

/**
 * Validate SKU format
 */
export const validateSKU = (sku: string): boolean => {
  if (!sku || sku.trim() === '') {
    return true; // SKU is optional
  }
  
  // SKU: 3-50 alphanumeric characters, may include hyphens and underscores
  const skuPattern = /^[A-Za-z0-9_-]{3,50}$/;
  return skuPattern.test(sku.trim());
};

/**
 * Get SKU validation error message
 */
export const getSKUError = (sku: string): string | null => {
  if (!sku || sku.trim() === '') {
    return null; // SKU is optional
  }
  
  const trimmed = sku.trim();
  
  if (trimmed.length < 3) {
    return 'SKU must be at least 3 characters';
  }
  
  if (trimmed.length > 50) {
    return 'SKU must be 50 characters or less';
  }
  
  if (!validateSKU(sku)) {
    return 'SKU can only contain letters, numbers, hyphens, and underscores';
  }
  
  return null;
};

// Combined Validation 

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validate receive/issue item
 */
export const validateReceiveItem = (item: {
  name?: string;
  barcode?: string;
  quantity?: string | number;
  category_id?: number | string;
  location_id?: number | string;
  itemsPerUnit?: string | number;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  
  if (item.name) {
    const nameError = getNameError(item.name, 'Product name');
    if (nameError) errors.name = nameError;
  }
  
  if (item.barcode) {
    const barcodeError = getBarcodeError(item.barcode);
    if (barcodeError) errors.barcode = barcodeError;
  }
  
  if (item.quantity !== undefined && item.quantity !== null && item.quantity !== '') {
    const quantityError = getQuantityError(item.quantity);
    if (quantityError) errors.quantity = quantityError;
  }
  
  if (item.category_id === '' || item.category_id === null || item.category_id === undefined) {
    errors.category_id = 'Category is required';
  }
  
  if (item.location_id === '' || item.location_id === null || item.location_id === undefined) {
    errors.location_id = 'Location is required';
  }
  
  if (item.itemsPerUnit !== undefined && item.itemsPerUnit !== null && item.itemsPerUnit !== '') {
    const itemsPerUnitError = getQuantityError(item.itemsPerUnit);
    if (itemsPerUnitError) errors.itemsPerUnit = itemsPerUnitError;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

